import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { describeNextRun, describeScheduleStatus } from '../../../components/admin/flowBilling/nextRun'
import {
  useBackfill,
  useFlowBillingConfig,
  useFlowBillingScheduleStatus,
  useMissingMonths,
  useUpdateFlowBillingConfig,
} from '../../../hooks/admin/useFlowBilling'
import { useGroups } from '../../../hooks/admin/useGroups'
import { RecipientsSection } from '../../../components/admin/flowBilling/RecipientsSection'
import { RunHistorySection } from '../../../components/admin/flowBilling/RunHistorySection'
import { ReportPreviewSection } from '../../../components/admin/flowBilling/ReportPreviewSection'
import { MissingReadingsSettingsSection } from '../../../components/admin/flowBilling/MissingReadingsSettingsSection'
import {
  Card,
  Field,
  NumberField,
  SelectField,
  TextField,
  Toggle,
} from '../../../components/admin/flowBilling/fields'
import { TIMEZONE_OPTIONS } from '../../../shared/types/settings'
import { getAvailableUnits } from '../../../utils/unitConversionUtils'
import { UnitCategoryId } from '../../../common/enums'
import type { FlowBillingConfig } from '../../../types/flowBilling'

/**
 * Reports ▸ Flowmeter ▸ Generation.
 *
 * Modelled on the system-health alert settings page: a plain useState mirror of
 * the fetched config, seeded by effect, committed with Save. It adds a Discard
 * button, which that page lacks — a settings form that can change a report
 * schedule should let somebody back out of a half-typed change without reloading.
 *
 * Validation is the server's, and its messages are surfaced verbatim. The
 * `min`/`max` here mirror the server's yup bounds so a bad value is caught
 * beside the field rather than as a bare 400 toast naming nothing.
 */

const HEX = /^#[0-9a-fA-F]{6}$/
const MAX_LOGO_BYTES = 256 * 1024

type FormState = Omit<FlowBillingConfig, 'id' | 'created' | 'updated'>

/**
 * The page in parts.
 *
 * Preview first, because looking at a month is what every visit starts with,
 * and generating is done from there once it looks right. History is its own
 * tab: it is a long table, and under Preview it pushed the month being looked
 * at off the screen.
 */
type TabId = 'preview' | 'history' | 'recipients' | 'branding' | 'settings'

const TABS: Array<{ id: TabId; label: string; hint: string }> = [
  { id: 'preview', label: 'Preview', hint: 'A month as it would be reported, and generating it' },
  { id: 'history', label: 'History', hint: 'Every report produced so far' },
  { id: 'recipients', label: 'Recipients', hint: 'Who receives it' },
  { id: 'branding', label: 'Branding', hint: 'What the PDF and the email look like' },
  { id: 'settings', label: 'Settings', hint: 'Group, units, period and when it runs' },
]

/** Tabs that contain form fields, and so need Save and Discard beneath them. */
const EDITING_TABS: TabId[] = ['settings', 'branding']

function TabBar({
  tab,
  onChange,
  dirty,
}: {
  tab: TabId
  onChange: (tab: TabId) => void
  dirty: boolean
}) {
  return (
    <div className="border-b border-gray-200 mb-6">
      <nav className="-mb-px flex flex-wrap gap-6">
        {TABS.map(({ id, label, hint }) => (
          <button
            key={id}
            type="button"
            title={hint}
            onClick={() => onChange(id)}
            className={`whitespace-nowrap border-b-2 pb-3 text-sm transition ${
              tab === id
                ? 'border-blue-600 text-blue-700 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
            }`}
          >
            {label}
            {/* Unsaved work is not visible from a tab that does not show it, so
                the tab that holds it says so. Without this, switching tabs
                looks like the change was discarded. */}
            {dirty && EDITING_TABS.includes(id) && (
              <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" />
            )}
          </button>
        ))}
      </nav>
    </div>
  )
}

/** Months per backfill request. Matches the server's default `limit`. */
const BACKFILL_BATCH = 12

/**
 * Closed months with readings and no report, offered above the history.
 * Silent when there are none: "nothing to backfill" is not worth a card.
 */
function BackfillNotice({ dirty }: { dirty: boolean }) {
  const missingMonths = useMissingMonths()
  const backfill = useBackfill()
  const missing = missingMonths.data ?? []
  if (missing.length === 0) return null

  // Named, not just counted: an administrator about to write twenty reports
  // into a district's history should see which months they are.
  const labels =
    missing.length > 6
      ? `${missing[0].label} – ${missing[missing.length - 1].label}`
      : missing.map((m) => m.label).join(', ')

  return (
    <div className="flex flex-wrap items-center gap-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
      <span className="mr-auto">
        {missing.length} closed month{missing.length === 1 ? ' has' : 's have'} readings but no report:{' '}
        {labels}. Backfilling makes them readable in the portal; nobody is emailed.
        {missing.length > BACKFILL_BATCH && ` ${BACKFILL_BATCH} at a time.`}
      </span>
      <button
        type="button"
        className="px-4 py-2 border border-blue-300 bg-white rounded-lg text-sm hover:bg-blue-100 disabled:opacity-50"
        disabled={backfill.isPending || dirty}
        onClick={() => backfill.mutate({})}
      >
        {backfill.isPending ? 'Backfilling…' : 'Backfill'}
      </button>
    </div>
  )
}

export default function FlowBillingPage() {
  const { data: config, isLoading } = useFlowBillingConfig()
  const { data: groups } = useGroups()
  const save = useUpdateFlowBillingConfig()
  const { data: scheduleStatus } = useFlowBillingScheduleStatus()
  const dueLine = describeScheduleStatus(scheduleStatus)

  const [form, setForm] = useState<FormState | null>(null)
  const [tab, setTab] = useState<TabId>('preview')

  useEffect(() => {
    if (config) {
      const { id, created, updated, ...rest } = config
      setForm(rest)
    }
  }, [config])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))

  const dirty = useMemo(() => {
    if (!config || !form) return false
    const { id, created, updated, ...stored } = config
    return JSON.stringify(stored) !== JSON.stringify(form)
  }, [config, form])

  const volumeUnits = useMemo(() => getAvailableUnits(UnitCategoryId.Volume), [])
  const sortedGroups = useMemo(
    () => [...(groups ?? [])].sort((a: any, b: any) => a.locationName.localeCompare(b.locationName)),
    [groups]
  )

  if (isLoading || !form) {
    return <div className="text-sm text-gray-500">Loading settings…</div>
  }

  const onLogoChosen = (file: File | undefined) => {
    if (!file) return
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      toast.error('The logo must be a PNG or JPEG.')
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(`The logo must be ${MAX_LOGO_BYTES / 1024} KB or smaller.`)
      return
    }
    const reader = new FileReader()
    reader.onload = () => set('brandLogo', String(reader.result))
    reader.onerror = () => toast.error('Could not read that file.')
    reader.readAsDataURL(file)
  }

  const accentValid = HEX.test(form.brandAccent)

  return (
    // max-w-6xl rather than 4xl: the run-history table carries six columns plus
    // per-row actions, and at 4xl it sat in a permanent horizontal scroll. The
    // settings forms inside each Card are still comfortable at this width.
    <div className="max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Flowmeter Report Generation</h1>
        <p className="text-sm text-gray-600 mt-1">
          A monthly water volume report for one group, emailed as a CSV and a PDF and readable in
          the portal.
        </p>
      </div>

      <TabBar tab={tab} onChange={setTab} dirty={dirty} />

      <div className="space-y-6">
        {tab === 'preview' && config && <ReportPreviewSection config={config} dirty={dirty} />}

        {tab === 'history' && (
          <>
            <BackfillNotice dirty={dirty} />
            <RunHistorySection />
          </>
        )}

        {tab === 'settings' && (
        <>
        <Card title="Report">
          <Toggle
            label="Enabled"
            checked={form.enabled}
            onChange={(v) => set('enabled', v)}
            hint="Nothing is generated or sent while this is off. It cannot be turned on until a group is selected and at least one recipient is enabled."
          />

          <Field label="Group" hint="Membership is read when the report runs, not when it is configured.">
            <SelectField
              value={form.locationId}
              onChange={(v) => set('locationId', v)}
              options={[
                { value: 0, label: '— select a group —' },
                ...sortedGroups.map((g: any) => ({
                  value: g.locationId as number,
                  label: `[${g.locationId}] ${g.locationName}`,
                })),
              ]}
            />
          </Field>

          <Field
            label="Reporting timezone"
            hint="Defines the period boundaries and every timestamp in the file."
          >
            <SelectField
              value={form.timezone}
              onChange={(v) => set('timezone', v)}
              options={TIMEZONE_OPTIONS.map((tz) => ({ value: tz.value, label: tz.label }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Volume unit">
              <SelectField
                value={form.unitId}
                onChange={(v) => set('unitId', v)}
                options={volumeUnits.map((u: any) => ({
                  value: u.unitId as number,
                  label: `${u.name} (${u.abbreviation})`,
                }))}
              />
            </Field>
            <Field label="Decimal places" hint="0–15.">
              <NumberField
                value={form.decimalPlaces}
                onChange={(v) => set('decimalPlaces', v ?? 6)}
                min={0}
                max={15}
              />
            </Field>
          </div>

          <Toggle
            label="Include devices with no readings"
            checked={form.includeDevicesWithoutReadings}
            onChange={(v) => set('includeDevicesWithoutReadings', v)}
            hint="A meter that reported nothing is a fact worth seeing, not a row to hide."
          />

          <Field
            label="File name pattern"
            hint={`Tokens: {group}, {YYYY}, {MM}. The .csv and .pdf extensions are not configurable.`}
          >
            <TextField
              value={form.filenamePattern}
              onChange={(v) => set('filenamePattern', v)}
              maxLength={128}
            />
          </Field>
        </Card>

        <Card title="Schedule">
          <Field label="Period">
            <SelectField
              value={form.periodType}
              onChange={(v) => set('periodType', v)}
              options={[
                { value: 'previous_month', label: 'Previous calendar month' },
                { value: 'rolling_days', label: 'Rolling window of N days' },
              ]}
            />
          </Field>

          {form.periodType === 'rolling_days' && (
            <Field label="Window (days)">
              <NumberField
                value={form.periodDays}
                onChange={(v) => set('periodDays', v ?? 30)}
                min={1}
                max={366}
              />
            </Field>
          )}

          <div className="grid grid-cols-3 gap-4">
            <Field label="Day of month" hint="1–28.">
              <NumberField
                value={form.generationDay}
                onChange={(v) => set('generationDay', v ?? 1)}
                min={1}
                max={28}
              />
            </Field>
            <Field label="Hour">
              <NumberField
                value={form.generationHour}
                onChange={(v) => set('generationHour', v ?? 12)}
                min={0}
                max={23}
              />
            </Field>
            <Field label="Minute">
              <NumberField
                value={form.generationMinute}
                onChange={(v) => set('generationMinute', v ?? 0)}
                min={0}
                max={59}
              />
            </Field>
          </div>
          <p className="text-xs text-gray-500">
            Local to the reporting timezone. Midday on the 1st is the default because it lets late
            SigFox traffic land before the month is totalled.
          </p>
          <p className="text-sm text-gray-700">
            <span className="text-gray-500">Next run</span>{' '}
            <span className="font-medium">{describeNextRun(form)}</span>{' '}
            <span className="text-gray-500">({form.timezone})</span>
          </p>
          {dueLine && (
            <p
              className={
                dueLine.tone === 'danger'
                  ? 'rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'
                  : 'rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'
              }
            >
              {dueLine.text}
            </p>
          )}
        </Card>

        <MissingReadingsSettingsSection />
        </>
        )}

        {tab === 'branding' && (
        <Card title="Report branding">
          <p className="text-sm text-gray-600 mb-4">
            Printed on the PDF and in the email. Branding is copied onto each run, so changing it
            here affects future reports only — a report already sent keeps the branding it carried.
          </p>

          <Field label="Wordmark">
            <TextField value={form.brandName} onChange={(v) => set('brandName', v)} maxLength={48} />
          </Field>

          <Field label="Accent colour" hint="#RRGGBB.">
            <div className="flex items-center gap-2">
              <input
                type="color"
                className="h-9 w-12 border border-gray-300 rounded"
                value={accentValid ? form.brandAccent : '#1a73e8'}
                onChange={(e) => set('brandAccent', e.target.value)}
                aria-label="Accent colour picker"
              />
              <input
                type="text"
                className={`w-32 border rounded px-3 py-2 text-sm ${
                  accentValid ? 'border-gray-300' : 'border-red-400'
                }`}
                value={form.brandAccent}
                onChange={(e) => set('brandAccent', e.target.value)}
              />
              {!accentValid && <span className="text-xs text-red-600">Must be #RRGGBB.</span>}
            </div>
          </Field>

          <Field label="Logo" hint="PNG or JPEG, up to 256 KB. Optional — the wordmark shows alone without one.">
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="text-sm"
                onChange={(e) => onLogoChosen(e.target.files?.[0])}
              />
              {form.brandLogo && (
                <button
                  type="button"
                  className="text-red-600 hover:underline text-sm"
                  onClick={() => set('brandLogo', null)}
                >
                  Clear
                </button>
              )}
            </div>
          </Field>

          <Field label="Footer line">
            <TextField
              value={form.reportFooter ?? ''}
              onChange={(v) => set('reportFooter', v || null)}
              maxLength={160}
              placeholder="Questions? reports@example.com"
            />
          </Field>

          <div className="border border-gray-200 rounded p-4 bg-gray-50">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">Preview</p>
            <div className="bg-white border border-gray-200 rounded p-3">
              <div className="flex items-center gap-3">
                {form.brandLogo && (
                  <img src={form.brandLogo} alt="" className="h-7 object-contain" />
                )}
                <span
                  className="text-lg font-bold"
                  style={{ color: accentValid ? form.brandAccent : '#1a73e8' }}
                >
                  {form.brandName || 'IoAg'}
                </span>
              </div>
              <div
                className="h-0.5 mt-2"
                style={{ background: accentValid ? form.brandAccent : '#1a73e8' }}
              />
              <p className="text-sm text-gray-800 mt-2">Group name — July 2026</p>
              {form.reportFooter && (
                <p className="text-[10px] text-gray-400 mt-3">{form.reportFooter}</p>
              )}
            </div>
          </div>
        </Card>
        )}

        {tab === 'recipients' && <RecipientsSection />}
      </div>

      {/* Only under the tabs that hold fields. A Save button on the history
          tab would be a button with nothing to do. */}
      {EDITING_TABS.includes(tab) && (
      <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 mt-6 py-4 flex items-center justify-end gap-3">
        {dirty && <span className="text-sm text-gray-500 mr-auto">Unsaved changes</span>}
        <button
          type="button"
          className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100 disabled:opacity-50"
          disabled={!dirty || save.isPending}
          onClick={() => {
            if (!config) return
            const { id, created, updated, ...stored } = config
            setForm(stored)
          }}
        >
          Discard
        </button>
        <button
          type="button"
          className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          disabled={!dirty || save.isPending || !accentValid}
          onClick={() => save.mutate(form)}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      )}
    </div>
  )
}
